import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiDroplet,
  FiFeather,
  FiHeart,
  FiMessageCircle,
  FiRefreshCw,
  FiRepeat,
  FiTruck,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { ProductCard } from "./Shop";
import "../css/main.scss";

const heroSlides = [
  {
    image: "/main/home-denim.jpg",
    eyebrow: "The denim edit",
    title: "DENIM",
    copy: "Authentic character. Everyday ease.",
  },
  {
    image: "/main/home-inspired.jpg",
    eyebrow: "Spring / Summer 2026",
    title: "GET INSPIRED",
    copy: "Easy layers for slower, brighter days.",
  },
  {
    image: "/main/home-outdoor.jpg",
    eyebrow: "Beyond the city",
    title: "OUTDOOR",
    copy: "Quiet utility, designed to move with you.",
  },
];

const values = [
  [FiFeather, "Material details", "Composition information is listed clearly on every product page."],
  [FiDroplet, "Colour notes", "Product colours are described clearly; screens may show small variations."],
  [FiRepeat, "Everyday selection", "Straightforward shapes are selected for a versatile wardrobe."],
  [FiHeart, "Here to help", "Our customer-care team can answer product and order questions."],
];

const serviceNotes = [
  [
    "Composition, fit and care information are grouped together so each item is easier to compare.",
    "Clear product details",
    "Before you choose",
  ],
  [
    "Available sizes and colours are shown directly on the product page before an item is added to the bag.",
    "Straightforward selection",
    "While you shop",
  ],
  [
    "Delivery, return and contact information remains easy to find whenever you need practical support.",
    "Accessible support",
    "After checkout",
  ],
];

const stories = [
  ["/main/story-materials.jpg", "Materials", "Why the fibre is only the beginning", "/about"],
  ["/main/story-journal.jpg", "Journal", "A study in natural colour and easy layers", "/shop?tag=New"],
  ["/main/story-craft.jpg", "Craft", "Inside the decisions behind a lasting garment", "/about"],
];

function Main() {
  const [activeSlide, setActiveSlide] = useState(0);
  const bestsellers = useMemo(
    () => productData.filter((product) => product.featured).slice(0, 4),
    []
  );

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");

    document.title = `${siteConfig.brandName} · ${siteConfig.tagline}`;
    description?.setAttribute("content", siteConfig.description);

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, []);

  const moveSlide = (direction) => {
    setActiveSlide(
      (current) =>
        (current + direction + heroSlides.length) % heroSlides.length
    );
  };

  return (
    <main className="home" id="main-content">
      <section className="home-hero" aria-label="Current collections">
        {heroSlides.map((slide, index) => (
          <article
            className={`home-hero__slide ${activeSlide === index ? "is-active" : ""}`}
            aria-hidden={activeSlide !== index}
            key={slide.title}
          >
            <img
              src={slide.image}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
            />
            <div className="home-hero__veil" />
            <div className="home-hero__content">
              <p>{slide.eyebrow}</p>
              {index === 0 ? <h1>{slide.title}</h1> : <h2>{slide.title}</h2>}
              <span>{slide.copy}</span>
              <div className="home-hero__actions">
                <Link
                  to="/shop?gender=Women"
                  tabIndex={activeSlide === index ? 0 : -1}
                >
                  Shop women
                </Link>
                <Link
                  to="/shop?gender=Men"
                  tabIndex={activeSlide === index ? 0 : -1}
                >
                  Shop men
                </Link>
              </div>
            </div>
          </article>
        ))}

        <div className="home-hero__controls">
          <button type="button" onClick={() => moveSlide(-1)} aria-label="Previous collection">
            <FiArrowLeft aria-hidden="true" />
          </button>
          <div className="home-hero__pagination" aria-label="Choose collection">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                className={activeSlide === index ? "is-active" : ""}
                aria-label={`Show ${slide.title}`}
                aria-current={activeSlide === index ? "true" : undefined}
                onClick={() => setActiveSlide(index)}
                key={slide.title}
              />
            ))}
          </div>
          <button type="button" onClick={() => moveSlide(1)} aria-label="Next collection">
            <FiArrowRight aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="home-mission" aria-labelledby="home-mission-title">
        <img
          src="/main/home-mission.jpg"
          alt="Fabric, paper patterns and sewing tools arranged on a studio worktable"
          loading="lazy"
        />
        <div className="home-mission__content">
          <p className="home-eyebrow">Thoughtfully in transit</p>
          <h2 id="home-mission-title">
            What is a true {siteConfig.brandName}?
          </h2>
          <p>
            It is a useful garment presented with clear details, an easy shape
            and a place in an everyday wardrobe.
          </p>
          <details>
            <summary>Discover our mission</summary>
            <p>
              We focus on clear product information, versatile styling and a
              calm shopping experience for everyday clothing.
            </p>
            <Link to="/about">Read the brand mission</Link>
          </details>
        </div>
      </section>

      <section className="home-section home-bestsellers" aria-labelledby="bestsellers-title">
        <div className="home-section__heading">
          <div>
            <p className="home-eyebrow">Most loved</p>
            <h2 id="bestsellers-title">Bestsellers</h2>
          </div>
          <Link className="home-text-link" to="/shop?sort=featured">
            View all pieces <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="home-product-grid">
          {bestsellers.map((product) => (
            <ProductCard product={product} key={product.id} />
          ))}
        </div>
      </section>

      <section className="home-values" aria-label="Our standards">
        <div className="home-values__grid">
          {values.map(([Icon, title, copy]) => (
            <article key={title}>
              <Icon aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-new" aria-label="New arrivals">
        <Link className="home-new__tile" to="/shop?gender=Women&q=New">
          <img src="/main/home-new-women.jpg" alt="Woman wearing cream and olive everyday layers" loading="lazy" />
          <span className="home-new__overlay" />
          <span className="home-new__content">
            <small>New in</small>
            <strong>Women</strong>
            <em>Discover the collection</em>
          </span>
        </Link>
        <Link className="home-new__tile" to="/shop?gender=Men&q=New">
          <img src="/main/home-new-men.jpg" alt="Man wearing a navy overshirt and stone trousers" loading="lazy" />
          <span className="home-new__overlay" />
          <span className="home-new__content">
            <small>New in</small>
            <strong>Men</strong>
            <em>Discover the collection</em>
          </span>
        </Link>
      </section>

      <section className="home-reviews home-section" aria-labelledby="reviews-title">
        <div className="home-reviews__intro">
          <p className="home-eyebrow">Shopping with us</p>
          <h2 id="reviews-title">What to expect.</h2>
          <p>Useful information at each stage of your order.</p>
        </div>
        <div className="home-reviews__grid">
          {serviceNotes.map(([copy, title, meta]) => (
            <figure key={title}>
              <div aria-hidden="true">—</div>
              <blockquote>{copy}</blockquote>
              <figcaption>
                <strong>{title}</strong>
                <span>{meta}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="home-service" aria-label="Service benefits">
        <article>
          <FiTruck aria-hidden="true" />
          <span><strong>Free delivery</strong><small>From {formatPrice(siteConfig.shipping.freeThreshold)}</small></span>
        </article>
        <article>
          <FiRefreshCw aria-hidden="true" />
          <span><strong>Easy returns</strong><small>{siteConfig.shipping.returnsWindow} days to decide</small></span>
        </article>
        <article>
          <FiMessageCircle aria-hidden="true" />
          <span><strong>Personal service</strong><small>Real help from our studio</small></span>
        </article>
      </section>

      <section className="home-denim" aria-labelledby="denim-title">
        <img src="/main/home-denim-feature.jpg" alt="Indigo denim look photographed in a quiet studio" loading="lazy" />
        <div>
          <p className="home-eyebrow">Indigo tones. Everyday character.</p>
          <h2 id="denim-title">Denim collection</h2>
          <p>Denim shapes selected for everyday movement.</p>
          <Link to="/shop?category=Denim">Explore denim</Link>
        </div>
      </section>

      <section className="home-section home-stories" aria-labelledby="stories-title">
        <div className="home-section__heading">
          <div>
            <p className="home-eyebrow">Notes from the studio</p>
            <h2 id="stories-title">Stories</h2>
          </div>
        </div>
        <div className="home-stories__grid">
          {stories.map(([image, category, title, to]) => (
            <article key={title}>
              <Link to={to} className="home-stories__image">
                <img src={image} alt="" loading="lazy" />
              </Link>
              <p>{category}</p>
              <h3><Link to={to}>{title}</Link></h3>
              <Link className="home-text-link" to={to}>Read more <FiArrowRight aria-hidden="true" /></Link>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Main;
